/**
 * License API Client
 * Handles all license-related API calls
 */

import apiClient, { formatApiError } from './client';

/**
 * @typedef {Object} LicenseStatusResponse
 * @property {string} benutzerId - User ID (GUID)
 * @property {string} lizenzId - License ID (GUID)
 * @property {string} lizenzKey - License key
 * @property {boolean} istAktiv - Is license active
 * @property {string} lizenzTyp - License type ("Free" or "Paid")
 * @property {number} maxEmailsProTag - Max emails per day
 * @property {number} maxEmailsProMonat - Max emails per month
 * @property {BenutzerSettings} benutzerSettings - User settings object
 */

/**
 * @typedef {Object} BenutzerSettings
 * @property {boolean} erinnerungVerschluesselnAlleEmpfaenger
 * @property {boolean} nichtNachPasswortFragenFuerGespeicherteEmpfaenger
 * @property {string|null} vorlageBetreff
 * @property {boolean} hasCustomTemplate
 * @property {boolean} betreffUnverschluesseltBehalten
 * @property {boolean} originalEmailEinbetten
 * @property {boolean} nurPdfDirektVerschluesseln
 * @property {boolean} entschluesseltGesendetSpeichern
 * @property {boolean} passwortOptionenGuiDeaktivieren
 * @property {string|null} optionenGuiPasswortHash
 * @property {string|null} optionenGuiPasswortSalt
 * @property {boolean} startbildschirmAnzeigen
 * @property {boolean} passwortmanagerAktiviert
 * @property {boolean} erinnerungEinmalVorVerschluesseln
 */

/**
 * Get license status for a user
 * Automatically creates user and assigns FREE license if user doesn't exist
 *
 * @param {string} oid - User's Graph ID (Azure AD)
 * @returns {Promise<{success: boolean, data: LicenseStatusResponse|null, message: string}>}
 */
export async function getLicenseStatus(oid) {
  try {
    const response = await apiClient.get(`/api/lizenz/status/${oid}`);

    // Backend returns ApiResponse wrapper
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Lizenzstatus erfolgreich abgerufen'
      };
    } else {
      return {
        success: false,
        data: null,
        message: response.data.message || 'Fehler beim Abrufen des Lizenzstatus'
      };
    }
  } catch (error) {
    console.error('Error fetching license status:', error);
    return {
      success: false,
      data: null,
      message: formatApiError(error, 'Fehler beim Abrufen des Lizenzstatus'),
    };
  }
}

/**
 * Activate a license for a user
 * Deactivates previous licenses and activates the new one
 *
 * @param {string} lizenzKey - License key to activate
 * @param {string} oid - User's Graph ID (Azure AD)
 * @returns {Promise<{success: boolean, data: string|null, message: string}>}
 */
export async function activateLicense(lizenzKey, oid) {
  try {
    const response = await apiClient.post('/api/lizenz/aktivieren', {
      lizenzKey: lizenzKey,
      oid: oid
    });

    // Backend returns ApiResponse wrapper
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Lizenz erfolgreich aktiviert'
      };
    } else {
      return {
        success: false,
        data: null,
        message: response.data.message || 'Fehler bei der Aktivierung'
      };
    }
  } catch (error) {
    console.error('Error activating license:', error);
    return {
      success: false,
      data: null,
      message: formatApiError(error, 'Fehler bei der Lizenzaktivierung'),
    };
  }
}
